/**
 * USDA Rural-Urban Continuum Codes (RUCC) 2023 — thin wrapper
 * Generated by data_raw/convert_usda_data.R on 2026-04-02
 * Data: 3233 counties in public/data/rucc.json
 *
 * Source: USDA Economic Research Service
 * https://www.ers.usda.gov/data-products/rural-urban-continuum-codes/
 *
 * Note: 2023 version raised the urban area threshold from 2,500 to 5,000
 * people, consistent with the Census Bureau's 2020 urban area redefinition.
 */

let _data = null;
let _loadPromise = null;

export function loadRuccData() {
  if (_data) return Promise.resolve();
  if (_loadPromise) return _loadPromise;
  _loadPromise = fetch(`${process.env.PUBLIC_URL}/data/rucc.json`)
    .then(r => {
      if (!r.ok) throw new Error(`Failed to load RUCC data (${r.status})`);
      return r.json();
    })
    .then(d => { _data = d; _loadPromise = null; })
    .catch(err => { _loadPromise = null; throw err; });
  return _loadPromise;
}

export const RUCC_DESCRIPTIONS = {
  1: "Metro — county in metro area of 1 million+ population",
  2: "Metro — county in metro area of 250,000–1 million population",
  3: "Metro — county in metro area of fewer than 250,000 population",
  4: "Nonmetro — urban population 20,000+, adjacent to a metro area",
  5: "Nonmetro — urban population 20,000+, not adjacent to a metro area",
  6: "Nonmetro — urban population 5,000–19,999, adjacent to a metro area",
  7: "Nonmetro — urban population 5,000–19,999, not adjacent to a metro area",
  8: "Nonmetro — completely rural or fewer than 5,000 urban, adjacent to a metro area",
  9: "Nonmetro — completely rural or fewer than 5,000 urban, not adjacent to a metro area"
};

export function getRUCC(stateFips, countyFips) {
  if (!_data) return null;
  const key = `${String(stateFips).padStart(2, "0")}${String(countyFips).padStart(3, "0")}`;
  return _data[key] ?? null;
}

export function getRUCCDescription(code) {
  return RUCC_DESCRIPTIONS[code] ?? "Unknown classification";
}

export function ruccToScore(rucc) {
  const map = { 1: 8, 2: 18, 3: 28, 4: 42, 5: 52, 6: 62, 7: 72, 8: 82, 9: 92 };
  return map[rucc] ?? null;
}

export function isRuralByRUCC(rucc) { return rucc >= 4; }

/**
 * Four-tier collapse of the nine RUCC codes — the palette the
 * choropleth, "Places Like This", and the search suggestions all share, so a
 * county reads the same shade wherever it appears. Returns CSS variables,
 * which carry the lightened dark-mode values defined in index.css.
 *
 * Distinct from getRUCCColor in App.js, which returns Tailwind badge classes.
 */
export function ruccTierColor(code) {
  if (code === null || code === undefined) return 'var(--color-ink-subtle)';
  if (code <= 3) return 'var(--tier-metro)';
  if (code <= 5) return 'var(--tier-urban)';
  if (code <= 7) return 'var(--tier-rural)';
  return 'var(--tier-very-rural)';
}

export function ruccTierLabel(code) {
  if (code === null || code === undefined) return '—';
  if (code <= 3) return 'Metro';
  if (code <= 5) return 'Urban';
  if (code <= 7) return 'Rural';
  return 'Very rural';
}

const ruralUrbanCodes = {
  RUCC_DESCRIPTIONS, getRUCC, getRUCCDescription, ruccToScore, isRuralByRUCC,
  ruccTierColor, ruccTierLabel,
};
export default ruralUrbanCodes;
